/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import axios from "axios";
import { Action, Dispatch } from "redux";

import {
  GET_ALL_OWNS,
  GET_CURRENT_OWN,
  UPDATE_OWN,
  SET_OWN_DATA,
  SET_OWN_TEST,
  GET_OWN_TEST,
  CHECKOUT_SUCCESS,
  CHECKOUT_FAILED,
  SHOW_TOAST,
  ACCESS_OWNS_FAILED,
  ApricotActionCreator,
  reqHeader,
  GET_STUDY_ITEMS,
  GET_STUDY_FAILED,
  CLEAR_STUDY_DATA,
  PHOENIX_NOP,
} from "./types";
import { sEnrollOk } from "assets/jss/views/ResStrings";

import { allOwnsLoaded, currentOwnLoaded } from "../selectors/ownSelector";
import { studyLoaded } from "../selectors/studySelector";

const inTest = process.env.NODE_ENV === "test";

// the 'api' passed in is the axiosInstance we created

export const addOwn: ApricotActionCreator = (
  uid: any,
  courses: any[],
) => async (dispatch: Dispatch<any>, getState, api): Promise<Action> => {
  try {
    api = api || axios;
    const res = await api({
      method: "post",
      url: `/api/ow/${uid}`,
      headers: reqHeader(getState),
      data: courses,
    });

    dispatch({
      type: SHOW_TOAST,
      payload: { type: "normal", message: sEnrollOk },
    });
    dispatch({
      type: CLEAR_STUDY_DATA,
      payload: {},
    });
    return dispatch({
      type: CHECKOUT_SUCCESS,
      payload: res.data,
    });
  } catch (err) {
    return dispatch({
      type: CHECKOUT_FAILED,
      payload: null,
    });
  }
};

export const getAllOwns: ApricotActionCreator = (uid: any) => async (
  dispatch: Dispatch<any>,
  getState,
  api,
): Promise<Action> => {
  try {
    if (!inTest && allOwnsLoaded(getState())) {
      return Promise.resolve() as any;
    }

    if (uid !== null) {
      api = api || axios; // in Test, api is not passed in.
      const res = await api.get(`/api/ow/${uid}`);
      return dispatch({
        type: GET_ALL_OWNS,
        payload: res.data,
      });
    } else {
      return dispatch({ type: GET_ALL_OWNS, payload: {} });
    }
  } catch (err) {
    return dispatch({
      type: ACCESS_OWNS_FAILED,
      payload: "",
    });
  }
};

export const getCurrentOwn: ApricotActionCreator = (
  uid: string,
  cid: string,
) => async (dispatch: Dispatch<any>, getState, api): Promise<Action> => {
  try {
    if (!inTest && currentOwnLoaded(getState(), { uid, cid })) {
      return Promise.resolve() as any;
    }

    if (uid !== null) {
      api = api || axios; // in Test, api is not passed in.
      const res = await api.get(`/api/ow/${uid}/${cid}`);

      return dispatch({
        type: GET_CURRENT_OWN,
        payload: res.data,
      });
    } else {
      return dispatch({ type: GET_CURRENT_OWN, payload: {} });
    }
  } catch (err) {
    return dispatch({
      type: ACCESS_OWNS_FAILED,
      payload: "",
    });
  }
};

export const updateOwn: ApricotActionCreator = (
  uid: string,
  cid: string,
  props: any,
) => async (dispatch: Dispatch<any>, getState, api): Promise<Action> => {
  try {
    api = api || axios; // in Test, api is not passed in.
    await api({
      method: "put",
      url: `/api/ow/${uid}/${cid}`,
      headers: reqHeader(getState),
      data: props,
    });

    return dispatch({
      type: UPDATE_OWN,
      payload: { cid, props }, // for update, use parameter input for reducer
    });
  } catch (err) {
    return dispatch({
      type: ACCESS_OWNS_FAILED,
      payload: "",
    });
  }
};

export const setOwnData: ApricotActionCreator = (
  uid: string,
  cid: string,
  lid: string,
  props: any,
  updateStore: boolean = true,
) => async (dispatch: Dispatch<any>, getState, api): Promise<Action> => {
  try {
    api = api || axios; // in Test, api is not passed in.
    await api({
      method: "put",
      url: `/api/ow/${uid}/${cid}/${lid}`,
      headers: reqHeader(getState),
      data: props,
    });

    return dispatch(
      updateStore
        ? { type: SET_OWN_DATA, payload: { cid, lid, props } }
        : { type: PHOENIX_NOP },
    );
  } catch (err) {
    return dispatch({
      type: ACCESS_OWNS_FAILED,
      payload: "",
    });
  }
};

export const setOwnTest: ApricotActionCreator = (
  uid: string,
  bid: string,
  data: any,
) => async (dispatch: Dispatch<any>, getState, api): Promise<Action> => {
  try {
    api = api || axios; // in Test, api is not passed in.
    await api({
      method: "put",
      url: `/api/ots/${uid}/${bid}`,
      headers: reqHeader(getState),
      data,
    });

    return dispatch({
      type: SET_OWN_TEST,
      payload: { bid, data },
    });
  } catch (err) {
    return dispatch({
      type: ACCESS_OWNS_FAILED,
      payload: "",
    });
  }
};

export const getOwnTest: ApricotActionCreator = (
  uid: string,
  bid: string,
  getDB: boolean,
) => async (dispatch: Dispatch<any>, getState, api): Promise<Action> => {
  try {
    api = api || axios; // in Test, api is not passed in.
    const data = getDB
      ? (
          await api({
            method: "get",
            url: `/api/ots/${uid}/${bid}`,
            headers: reqHeader(getState),
          })
        ).data
      : null;

    return dispatch({
      type: GET_OWN_TEST,
      payload: { bid, data },
    });
  } catch (err) {
    return dispatch({
      type: ACCESS_OWNS_FAILED,
      payload: "",
    });
  }
};

export const getStudyItems: ApricotActionCreator = (
  uid: string,
  type: number,
) => async (dispatch: Dispatch<any>, getState, api): Promise<Action> => {
  try {
    if (!inTest && studyLoaded(getState(), type)) {
      return Promise.resolve() as any;
    }

    api = api || axios; // in Test, api is not passed in.
    const { data } = await api({
      method: "get",
      url: `/api/ox/${uid}/${type}`,
      headers: reqHeader(getState),
    });

    return dispatch({
      type: GET_STUDY_ITEMS,
      payload: { type, data },
    });
  } catch (err) {
    return dispatch({
      type: GET_STUDY_FAILED,
      payload: "",
    });
  }
};

export type ApricotAction = ApricotActionCreator;
